import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { financialsApi } from '@/lib/financialsApi';
import { toNumber } from '@/lib/number';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { useMembership } from '@/hooks/useMembership';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import {
  GitCompareArrows,
  CheckCircle2,
  Trophy,
  Lightbulb,
} from 'lucide-react';
import type { ApiResponse } from '@/types/api';
import type { CycleComparison } from '@/types/financials';

export const Route = createFileRoute('/_app/financials/comparison')({
  component: ComparisonPage,
});

interface Cycle {
  id: string;
  pond_name: string;
  cycle_number: number;
  status: string;
  stocking_date: string | null;
}

function formatPHP(value: number): string {
  return value.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' });
}

function ComparisonPage() {
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();
  const { membership } = useMembership();

  const [selectedCycleIds, setSelectedCycleIds] = useState<string[]>([]);

  // Fetch all cycles for selection
  const { data: cyclesData, isLoading: cyclesLoading } = useQuery({
    queryKey: ['cycles', membership?.farmId],
    queryFn: async () => {
      const response = await api.get<{ data: Cycle[] }>(
        `/api/v1/farms/${membership?.farmId}/cycles`
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    enabled: !!membership?.farmId,
  });

  const cycles = useMemo(() => cyclesData || [], [cyclesData]);

  // Fetch comparison data when cycles are selected
  const {
    data: comparisonData,
    isLoading: comparisonLoading,
    error: comparisonError,
    refetch: refetchComparison,
  } = useQuery<ApiResponse<CycleComparison>>({
    queryKey: ['financials', 'comparison', farmId, selectedCycleIds],
    queryFn: () => financialsApi.getComparison(farmId!, selectedCycleIds),
    staleTime: 60 * 1000,
    enabled: !!farmId && selectedCycleIds.length >= 2,
  });

  const comparison = comparisonData?.data;

  function toggleCycle(cycleId: string) {
    setSelectedCycleIds((prev) =>
      prev.includes(cycleId)
        ? prev.filter((id) => id !== cycleId)
        : [...prev, cycleId]
    );
  }

  // Group cycles by status for the selector
  const groupedCycles = useMemo(() => {
    const active = cycles.filter((c) => c.status === 'active');
    const completed = cycles.filter(
      (c) => c.status === 'completed' || c.status === 'harvested'
    );
    const other = cycles.filter(
      (c) => c.status !== 'active' && c.status !== 'completed' && c.status !== 'harvested'
    );
    return { active, completed, other };
  }, [cycles]);

  if (isFarmLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cycle selector */}
      <Card>
        <Card.Header>
          <span className="flex items-center gap-2">
            <GitCompareArrows className="h-4 w-4 text-gray-400" />
            Select Cycles to Compare
          </span>
        </Card.Header>
        <Card.Body>
          {cyclesLoading ? (
            <div className="flex items-center gap-2 py-4">
              <LoadingSpinner size={16} />
              <span className="text-sm text-gray-500">Loading cycles...</span>
            </div>
          ) : cycles.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No cycles found for this farm.</p>
          ) : (
            <div className="space-y-4">
              {groupedCycles.active.length > 0 && (
                <CycleGroup
                  label="Active"
                  cycles={groupedCycles.active}
                  selectedIds={selectedCycleIds}
                  onToggle={toggleCycle}
                />
              )}
              {groupedCycles.completed.length > 0 && (
                <CycleGroup
                  label="Completed"
                  cycles={groupedCycles.completed}
                  selectedIds={selectedCycleIds}
                  onToggle={toggleCycle}
                />
              )}
              {groupedCycles.other.length > 0 && (
                <CycleGroup
                  label="Other"
                  cycles={groupedCycles.other}
                  selectedIds={selectedCycleIds}
                  onToggle={toggleCycle}
                />
              )}

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  {selectedCycleIds.length} cycle{selectedCycleIds.length !== 1 ? 's' : ''} selected
                  {selectedCycleIds.length < 2 && ' (select at least 2)'}
                </p>
                {selectedCycleIds.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedCycleIds([])}
                  >
                    Clear selection
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Comparison results */}
      {selectedCycleIds.length >= 2 && (
        <>
          {comparisonLoading && (
            <div className="flex flex-col items-center justify-center h-40 gap-4">
              <LoadingSpinner size={24} className="text-primary-600" />
              <p className="text-sm text-gray-500">Comparing cycles...</p>
            </div>
          )}

          {comparisonError && (
            <ErrorMessage
              message={
                comparisonError instanceof Error
                  ? comparisonError.message
                  : 'Failed to compare cycles'
              }
              onRetry={() => refetchComparison()}
            />
          )}

          {comparison && <ComparisonResults comparison={comparison} />}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Cycle selection group
// ============================================================================

interface CycleGroupProps {
  label: string;
  cycles: Cycle[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

function CycleGroup({ label, cycles, selectedIds, onToggle }: CycleGroupProps) {
  return (
    <div>
      <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
        {label}
      </h3>
      <div className="flex flex-wrap gap-2">
        {cycles.map((cycle) => {
          const isSelected = selectedIds.includes(cycle.id);
          return (
            <button
              key={cycle.id}
              onClick={() => onToggle(cycle.id)}
              className={`
                inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all
                ${
                  isSelected
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              {isSelected && <CheckCircle2 className="h-4 w-4 text-primary-600" />}
              <span className="font-medium">{cycle.pond_name}</span>
              <span className="text-gray-400">#{cycle.cycle_number}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Comparison results
// ============================================================================

function ComparisonResults({ comparison }: { comparison: CycleComparison }) {
  const { cycles, best_performer, insights } = comparison;

  return (
    <div className="space-y-6">
      {/* Best performer badges */}
      {best_performer && (
        <Card>
          <Card.Header>
            <span className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-yellow-500" />
              Best Performers
            </span>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {best_performer.lowest_cost_per_kg && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
                  <Badge variant="success" size="md">Lowest Cost/kg</Badge>
                  <span className="text-sm text-gray-700">
                    {getCycleName(cycles, best_performer.lowest_cost_per_kg)}
                  </span>
                </div>
              )}
              {best_performer.highest_roi && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50">
                  <Badge variant="ok" size="md">Highest ROI</Badge>
                  <span className="text-sm text-gray-700">
                    {getCycleName(cycles, best_performer.highest_roi)}
                  </span>
                </div>
              )}
              {best_performer.highest_profit && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50">
                  <Badge variant="success" size="md">Highest Profit</Badge>
                  <span className="text-sm text-gray-700">
                    {getCycleName(cycles, best_performer.highest_profit)}
                  </span>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Comparison table */}
      <Card>
        <Card.Header>Side-by-Side Comparison</Card.Header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-gray-500">
                <th className="px-5 py-3 font-medium">Cycle</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium text-right">DOC</th>
                <th className="px-5 py-3 font-medium text-right">Total Cost</th>
                <th className="px-5 py-3 font-medium text-right">Revenue</th>
                <th className="px-5 py-3 font-medium text-right">Profit</th>
                <th className="px-5 py-3 font-medium text-right">Harvest (kg)</th>
                <th className="px-5 py-3 font-medium text-right">Cost/kg</th>
                <th className="px-5 py-3 font-medium text-right">FCR</th>
                <th className="px-5 py-3 font-medium text-right">Survival</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {cycles.map((cycle) => {
                const cost = toNumber(cycle.total_cost);
                const revenue = toNumber(cycle.total_revenue);
                const profit = toNumber(cycle.profit);
                const harvestKg = toNumber(cycle.harvest_kg);
                const costPerKg =
                  cycle.cost_per_kg != null ? toNumber(cycle.cost_per_kg) : null;

                return (
                  <tr key={cycle.cycle_id} className="hover:bg-gray-50/50">
                    <td className="px-5 py-3">
                      <span className="font-medium text-gray-900">
                        {cycle.cycle_name || 'Unnamed'}
                      </span>
                      {cycle.pond_name && (
                        <span className="ml-1 text-xs text-gray-400">
                          ({cycle.pond_name})
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <ComparisonStatusBadge status={cycle.status} />
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">
                      {cycle.doc ?? '--'}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">
                      {formatPHP(cost)}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">
                      {formatPHP(revenue)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span
                        className={
                          profit >= 0
                            ? 'text-emerald-600 font-medium'
                            : 'text-red-600 font-medium'
                        }
                      >
                        {formatPHP(profit)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">
                      {harvestKg > 0 ? harvestKg.toLocaleString() : '--'}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">
                      {costPerKg != null ? formatPHP(costPerKg) : '--'}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">
                      {cycle.fcr != null ? cycle.fcr.toFixed(2) : '--'}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-600">
                      {cycle.survival_pct != null
                        ? `${cycle.survival_pct.toFixed(0)}%`
                        : '--'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Insights */}
      {insights.length > 0 && (
        <Card>
          <Card.Header>
            <span className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-yellow-500" />
              Insights
            </span>
          </Card.Header>
          <Card.Body>
            <ul className="space-y-2">
              {insights.map((insight, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary-400 shrink-0" />
                  {insight}
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

function ComparisonStatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="neutral">Unknown</Badge>;
  switch (status) {
    case 'active':
      return <Badge variant="ok" dot>Active</Badge>;
    case 'completed':
    case 'harvested':
      return <Badge variant="success" dot>Completed</Badge>;
    case 'cancelled':
      return <Badge variant="critical" dot>Cancelled</Badge>;
    default:
      return <Badge variant="neutral">{status}</Badge>;
  }
}

function getCycleName(
  cycles: CycleComparison['cycles'],
  cycleId: string
): string {
  const cycle = cycles.find((c) => c.cycle_id === cycleId);
  if (!cycle) return cycleId;
  const name = cycle.cycle_name || 'Unnamed';
  return cycle.pond_name ? `${name} (${cycle.pond_name})` : name;
}
